// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0
import { getApiUrl } from '../config.js';

import { post } from './CoreService';

/**
 * ChatService - Handles chat message streaming with authentication and reasoning support
 */

/**
 * Sends a chat message and returns a streaming response with reasoning support
 * @param {Object} newMessageObj - The new message object {content: string, role: string}
 * @param {Array<Object>} chatHistory - Previous chat messages
 * @param {string} languageModel - Name of the currently selected LLM
 * @param {Object} callbacks - Callback functions for streaming events
 * @param {Function} callbacks.onReasonChunk - Called with each reason chunk (content: string)
 * @param {Function} callbacks.onResponseChunk - Called with each response chunk (content: string)
 * @param {Function} callbacks.onError - Called when an error occurs (error: {type: string, message: string})
 * @param {Function} callbacks.onComplete - Called when streaming is complete
 * @returns {Promise<void>}
 */
export const sendChatMessage = async (
  newMessageObj,
  chatHistory,
  languageModel,
  callbacks = {},
) => {
  const {
    onReasonChunk = () => {},
    onResponseChunk = () => {},
    onError = () => {},
    onComplete = () => {},
  } = callbacks;

  try {
    const response = await post(
      `${getApiUrl() || ''}/experimental/agentic/completion/stream`,
      {
        new_message: newMessageObj,
        chat_history: chatHistory,
        language_model: languageModel,
        request_timestamp: Date.now(),
      },
    );

    if (response.status !== 200) {
      let detail = 'Fehler beim Abrufen der Antwort';
      try {
        const res = await response.json();
        detail = res.detail || detail;
      } catch (_error) {
        const raw = await response.text().catch(() => '');
        if (raw && raw.trim().length > 0) {
          detail = raw;
        }
      }
      throw new Error(detail);
    }

    // Stream processing with reasoning support
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        onComplete();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      let lines = buffer.split('\n');
      buffer = lines.pop(); // last line may be incomplete

      for (const line of lines) {
        if (!line.trim()) continue;

        let data;
        try {
          data = JSON.parse(line);
        } catch (e) {
          continue;
        }

        if (data.type === 'error') {
          onError({
            type: 'ERROR',
            message: data.error,
          });
          return;
        }

        if (data.type === 'reason') {
          onReasonChunk(data.content);
        } else if (data.type === 'response') {
          if (data.finish_reason === 'stop') {
            onComplete();
            break;
          }
          onResponseChunk(data.content);
        }
      }
    }
  } catch (err) {
    onError({
      type: 'ERROR',
      message: err.message,
    });
  }
};
